import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import liff from "@line/liff";
import { LoadingOverlay } from "@/apps/@components";
import { getAssetUrl } from "@utils/getAssetUrl";
import { api, ApiError } from "@utils/index";
import { useUserEvent } from "@/apps/@hooks/useUserEvent";
import { useWaitingInPage } from "@/apps/@hooks/useWaitingInPage";
import "./Splash2026Page.scss";

const ASSET_BASE = "/images/campaign/summer_splash_campaign";
const PAGE_PATH = "/campaign/splash-2026";
// 給 BindingProvider 識別「綁完要回 splash 2026」的 from 值
const BINDING_FROM = "splash-2026";

const stickers = [
	{ src: `${ASSET_BASE}/stickers_1.gif` },
	{ src: `${ASSET_BASE}/stickers_2.gif` },
	{ src: `${ASSET_BASE}/stickers_3.gif` },
	{ src: `${ASSET_BASE}/stickers_4.gif` },
	{ src: `${ASSET_BASE}/stickers_5.gif` },
	{ src: `${ASSET_BASE}/stickers_6.gif` },
];

type Phase = "checking" | "requesting" | "blocked" | "ready";

const closeLiffIfPossible = () => {
	if (liff.isInClient()) {
		liff.closeWindow();
	}
};

export default function Splash2026Page() {
	const navigate = useNavigate();
	const { ensureToken, logPage, logEvent } = useUserEvent();
	const [phase, setPhase] = useState<Phase>("checking");
	const [analyticsReady, setAnalyticsReady] = useState(false);
	const autoRequestedRef = useRef(false);

	// analytics token 拿到後才打 page view
	useEffect(() => {
		if (!analyticsReady) return;
		logPage({ path: PAGE_PATH });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [analyticsReady]);

	// 每 5 秒打一次停留事件，等 analytics 初始化好才開始
	useWaitingInPage({
		enabled: analyticsReady,
		getPath: () => PAGE_PATH,
		logEvent,
	});

	const handleShare = () => {
		logEvent({
			category: "cta",
			path: PAGE_PATH,
			action: "share",
			event: "cta_share",
		});
		navigate("/share-to-friends?share_code=SummerSplash2026Sticker");
	};

	const handleStickerClick = (stickerIndex: number) => {
		logEvent({
			category: "gif",
			path: PAGE_PATH,
			action: "focus",
			event: "focus_gif",
			value: String(stickerIndex),
		});
	};

	// 出錯時直接跳轉至 LIFF_URL，整個 LIFF 流程重跑一次
	const restartLiff = (reason: string, err?: unknown) => {
		console.error(`Splash 2026: ${reason}`, err);
		const liffUrl = process.env.REACT_APP_LIFF_URL;
		if (liffUrl) {
			window.location.href = liffUrl;
		} else {
			// env 缺失就退回會員中心
			navigate("/profile", { replace: true });
		}
	};

	const checkFriendship = async (): Promise<boolean> => {
		setPhase("checking");
		try {
			const friendship = await liff.getFriendship();
			if (friendship.friendFlag) {
				return true;
			}
			setPhase("blocked");
			return false;
		} catch (err) {
			restartLiff("getFriendship failed", err);
			return false;
		}
	};

	// 取得主 api_token（splash 在 LoginGuard 外，必須自己 init，不然 /profile 會 401）
	const ensureApiToken = async () => {
		try {
			const accessToken = liff.getAccessToken() ?? "";
			const profile = await liff.getProfile();
			const res = await api.post("/login", {
				line_uid: profile.userId,
				access_token: accessToken,
				friendship: true, // 此時已通過 checkFriendship
				friendship_status_changed: false,
			});
			const apiToken = (res as { access_token?: string })?.access_token;
			if (apiToken) {
				localStorage.setItem("api_token", apiToken);
				console.log("[splash] api_token stored");
			} else {
				console.warn("[splash] /login returned no access_token", res);
			}
		} catch (err) {
			console.error("[splash] ensureApiToken failed", err);
			// 不向外丟，讓後續 checkBinding 自然走錯誤分支
		}
	};

	// 檢查是否已綁定會員；未綁定就帶 from 跳到 /binding，綁完會回來
	const checkBinding = async (): Promise<boolean> => {
		try {
			await api.get("/profile");
			return true;
		} catch (err) {
			const errorCode =
				(err as { response?: { data?: { error?: { code?: number } } } })
					?.response?.data?.error?.code;
			if (errorCode === ApiError.USER_NOT_BINDED) {
				navigate(`/binding?from=${BINDING_FROM}`);
				return false;
			}
			restartLiff("checkBinding failed", err);
			return false;
		}
	};

	// checkFriendship + ensureApiToken + checkBinding 都過 → setPhase("ready")
	const runEntryChecks = async (): Promise<void> => {
		const isFriend = await checkFriendship();
		if (!isFriend) return;
		// 確保 api_token 存在，後續 /profile 才能 authorized
		await ensureApiToken();
		const isBound = await checkBinding();
		if (!isBound) return;
		setPhase("ready");
	};

	const triggerRequestFriendship = async () => {
		setPhase("requesting");
		try {
			await liff.requestFriendship();
			// 加完好友 → 用統一流程繼續檢查 token + binding
			const isFriend = await checkFriendship();
			if (!isFriend) {
				// 使用者拒絕加好友 → 關閉 LIFF
				closeLiffIfPossible();
				return;
			}
			await ensureApiToken();
			const isBound = await checkBinding();
			if (!isBound) return; // 已導去 /binding
			setPhase("ready");
		} catch (err) {
			console.error("requestFriendship failed:", err);
			// 開啟視窗失敗也直接關閉
			closeLiffIfPossible();
		}
	};

	useEffect(() => {
		let cancelled = false;

		(async () => {
			try {
				const liffId = process.env.REACT_APP_LIFF_ID;
				if (!liffId) {
					if (!cancelled) {
						restartLiff("Missing REACT_APP_LIFF_ID");
					}
					return;
				}
				await liff.init({ liffId });
				if (cancelled) return;

				if (!liff.isLoggedIn()) {
					liff.login({ redirectUri: window.location.href });
					return;
				}

				// 先拿 analytics token，後續 logPage / logEvent 才會 authorized
				await ensureToken();
				if (cancelled) return;
				setAnalyticsReady(true);

				await runEntryChecks();
			} catch (err) {
				if (cancelled) return;
				restartLiff("LIFF init failed", err);
			}
		})();

		return () => {
			cancelled = true;
		};
		// restartLiff / checkFriendship 在 component 內定義，僅初始化跑一次
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// 一進入 blocked 自動彈一次加好友視窗（auto ref 確保只彈一次，避免無限循環）
	useEffect(() => {
		if (phase === "blocked" && !autoRequestedRef.current) {
			autoRequestedRef.current = true;
			triggerRequestFriendship();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [phase]);

	const isReady = phase === "ready";

	return (
		<div
			className="splash-2026-page"
			style={{
				backgroundImage: `url(${getAssetUrl(`${ASSET_BASE}/background.png`)})`,
			}}
		>
			<div className="dim-overlay" />

			<img
				className="tb-logo"
				src={getAssetUrl(`${ASSET_BASE}/tb_logo.png`)}
				alt="TORY BURCH"
			/>
			<img
				className="splash-logo"
				src={getAssetUrl(`${ASSET_BASE}/logo.png`)}
				alt="Splash"
			/>

			{isReady && (
				<>
					<img
						className="subtitle"
						src={getAssetUrl(`${ASSET_BASE}/text_1.png`)}
						alt="六款 SPLASH 動態貼圖"
					/>

					<div className="frame">
						<img
							className="hint"
							src={getAssetUrl(`${ASSET_BASE}/text_2.png`)}
							alt="長按圖片即可下載"
						/>
						<div className="stickers-grid">
							{stickers.map((sticker, index) => (
								<div
									key={sticker.src}
									className="sticker"
									onClick={() => handleStickerClick(index + 1)}
								>
									<img
										src={getAssetUrl(sticker.src)}
										alt={`splash sticker ${index + 1}`}
									/>
								</div>
							))}
						</div>
					</div>

					<button
						type="button"
						className="share-button"
						onClick={handleShare}
						aria-label="分享好友"
					>
						<img
							src={getAssetUrl(`${ASSET_BASE}/btn_share.png`)}
							alt="分享好友"
						/>
					</button>
				</>
			)}

			<LoadingOverlay
				visible={
					phase === "checking" ||
					phase === "requesting" ||
					phase === "blocked"
				}
			/>
		</div>
	);
}
